import * as arrays from '../common/arrays';
import { checkArgument, checkExists } from '../common/asserts';
import { deepEqual } from '../common/comparisons';

import { AnyProperties, Properties } from './elements';

export const FRAGMENT_MARKER = Symbol();

type Handle = {__brand: 'Handle'} & {
  id: number;
};

interface VElement {
  tag: string|(typeof FRAGMENT_MARKER);
  children: VElementOrPrimitive[];
  handle: Handle;
  key: string|undefined;
  props: Properties;
  childTrace: Handle[];
  factorySource: FactorySource|undefined;
}

export type VElementOrPrimitive = VElement|number|string;

/**
 * What a function component returns. Like React, it may return an array of siblings, which render
 * as a fragment would. Nested arrays flatten, as children do.
 */
export type Rendered = VElementOrPrimitive|readonly Rendered[];

export type ElementFactory = (
  props: Properties,
  state: unknown|undefined,
  updateState: (newState: unknown) => void) => Rendered;

interface FactorySource {
  factory: ElementFactory;
  children: VElementOrPrimitive[];
  props: Properties;
  state: unknown;
};

export interface Listener {
  createdElement(element: Element, props: Properties): void;
  patchedElement(element: Element, from: Properties, to: Properties): void;
  // Called once for the root of each removed subtree. It is not called for the descendants.
  removedNode(node: Node): void;
}

// Physical elements track the actual DOM elements that were created based upon a virtual element.
interface PhysicalElement {
  parent: Element;
  self: Node|undefined; // undefined in the case of a fragment element
  placeholder: Node|undefined; // set in empty fragments
  childHandles: Handle[];
  childTrace: Handle[];
  factorySource: FactorySource|undefined;
  key: string|undefined;
  props: Properties;
}

const listeners: Listener[] = [];

export function addListener(listener: Listener): void {
  listeners.push(listener);
}

export function createVirtualElement(
    element: keyof HTMLElementTagNameMap|ElementFactory,
    props: Properties|null,
    ...children: Array<VElementOrPrimitive|VElementOrPrimitive[]>): VElementOrPrimitive {
  const handle = createHandle();
  const flatChildren = deepFlatten(children);

  if (element instanceof Function) {
    const updateState = (newState: unknown) => {
      const physical = createdElements.get(handle);
      if (!physical) {
        return;
      }

      // Check to see if the element we're updating even still exists.
      if (!physical.parent.contains(findLastChildOrPlaceholder(physical))) {
        return;
      }

      if (creationTrace.length > 0) {
        console.error("creationTrace already set");
      }

      creationTrace.push([]);
      lastCreationTrace.push([...physical.childTrace]);
      let result;
      try {
        result = wrap(element(factoryProps(props, flatChildren), newState, updateState));
      } catch (e: unknown) {
        creationTrace.pop();
        throw e;
      } finally {
        lastCreationTrace.pop();
      }

      result.handle = handle;
      result.key = props?.key;
      result.childTrace = checkExists(creationTrace.pop());
      result.factorySource = {
        factory: element,
        children: flatChildren,
        props: props ?? {},
        state: newState,
      };
      patchChildren(physical.parent, [handle], [result], /* placeholder= */ undefined);
    };

    const lastTrace = lastCreationTrace[lastCreationTrace.length - 1];
    const lastPhysical = lastTrace ? claimPrior(lastTrace, element, props?.key) : undefined;

    let state;
    let priorChildTrace: Handle[];
    if (lastPhysical) {
      state = lastPhysical.factorySource?.state;
      // Same factory at this position: reuse its children's trace so nested
      // function-element state is preserved across re-renders.
      priorChildTrace = [...lastPhysical.childTrace];
    } else {
      // A different element (or nothing) previously occupied this position. Its
      // children are unrelated to ours, so we must NOT scope our nested lookups
      // to its childTrace — otherwise a same-typed descendant (e.g. a table
      // rendered in both this route view and the one we navigated away from)
      // would adopt the old view's state. Start fresh.
      state = undefined;
      priorChildTrace = [];
    }

    // Push ourselves into our parent's trace, if it exists.
    creationTrace[creationTrace.length - 1]?.push(handle);

    creationTrace.push([]);
    // Scope nested function elements' state lookups to OUR previous childTrace
    // — otherwise a Leaf rendered inside Middle would shift entries off
    // Grandparent's trace, corrupting Sibling's state lookup. updateState pushes
    // for re-renders; createVirtualElement has to do it for the in-line render
    // path that runs as part of an outer factory.
    lastCreationTrace.push(priorChildTrace);
    let result;
    try {
      result =
          wrap(
              element(factoryProps(props, flatChildren), state, updateState));
    } catch (e: unknown) {
      creationTrace.pop();
      lastCreationTrace.pop();
      throw e;
    }
    lastCreationTrace.pop();
    result.childTrace = checkExists(creationTrace.pop());
    result.handle = handle;
    result.key = props?.key;
    result.factorySource = {
      factory: element,
      children: flatChildren,
      props: props ?? {},
      state,
    };
    return result;
  } else {
    return {
      tag: element,
      children: flatChildren,
      handle,
      key: props?.key,
      props: props ?? {},
      childTrace: [],
      factorySource: undefined,
    };
  }
}

// Takes the first element in the previous render's trace made by the same factory with the same
// key, dropping the entries before it. A component that appears conditionally finds no match and
// takes nothing, or else every function element after it would read the state of the one before.
function claimPrior(
    trace: Handle[], factory: ElementFactory, key: string|undefined): PhysicalElement|undefined {
  const index = trace.findIndex(handle => {
    const physical = createdElements.get(handle);
    return physical?.key === key && deepEqual(physical?.factorySource?.factory, factory);
  });
  if (index < 0) {
    return undefined;
  }
  const physical = createdElements.get(trace[index]);
  trace.splice(0, index + 1);
  return physical;
}

export function appendElement(parent: Element, child: VElementOrPrimitive): void {
  appendChildrenToRoot([child], [maybeCreateHandle(child)], parent);
}

export function hydrateElement(parent: Element, to: VElementOrPrimitive): void {
  const children = [...parent.childNodes];
  checkArgument(children.length < 2, 'Cannot have more than one child');
  if (children.length === 0) {
    parent.appendChild(new Text(''));
  }

  try {
    hydrateElementRecursively(to, maybeCreateHandle(to), parent, /** left= */ undefined);
  } catch (e: unknown) {
    console.error('Error while hydrating page');
    throw e;
  }
}

function hydrateElementRecursively(
    element: VElementOrPrimitive, handle: Handle, parent: Element, left: Node|undefined): {
      childHandles: Handle[];
      last: Node;
    } {
  if (!(element instanceof Object)) {
    let node: Node;
    if (element === '') {
      node = new Text('');
      parent.insertBefore(node, nodeAfter(parent, left));
    } else {
      node = checkExists(nodeAfter(parent, left));
      checkArgument(node instanceof Text, 'Node should be text');
      // When passed from the server, \r\n turns into \n
      const need = String(element).replaceAll('\r\n', '\n');
      const current = node.textContent ?? '';
      checkArgument(current.startsWith(need), 'Text should match');
      if (current.length > need.length && current.startsWith(need)) {
        const actual = new Text(need);
        parent.insertBefore(actual, node);
        node.textContent = current.substring(need.length);
        node = actual;
      }
    }

    createdElements.set(
        handle, {
          parent,
          self: node,
          placeholder: undefined,
          childHandles: [],
          childTrace: [],
          factorySource: undefined,
          key: undefined,
          props: {},
        });
    return {
      childHandles: [],
      last: node,
    };
  }

  if (element.tag === FRAGMENT_MARKER) {
    const childHandles = [];
    let childLeft = left;
    for (const child of element.children) {
      const handle = maybeCreateHandle(child);
      const r = hydrateElementRecursively(child, handle, parent, childLeft);
      childHandles.push(handle);
      childLeft = r.last ?? childLeft;
    }

    const placeholder = new Text('');
    if (childHandles.length === 0) {
      parent.insertBefore(placeholder, nodeAfter(parent, left));
      childLeft = placeholder;
    }

    createdElements.set(
        handle, {
          parent,
          self: undefined,
          placeholder,
          childHandles,
          childTrace: element.childTrace,
          factorySource: element.factorySource,
          key: element.key,
          props: element.props,
        });
    return {
      childHandles,
      last: checkExists(childLeft),
    };
  } else {
    const node = checkExists(nodeAfter(parent, left)) as Element;
    checkArgument(element.tag === node.tagName.toLowerCase());

    const childHandles = [];
    let childLeft = undefined;
    for (const child of element.children) {
      const handle = maybeCreateHandle(child);
      const r = hydrateElementRecursively(child, handle, node, childLeft);
      childHandles.push(handle);
      childLeft = r.last ?? childLeft;
    }

    // The server leaves indeterminate out because HTML only has the property.
    const props = element.props as AnyProperties;
    if (node instanceof HTMLInputElement && 'indeterminate' in props && props.indeterminate) {
      node.indeterminate = true;
    }

    createdElements.set(
        handle, {
          parent,
          self: node,
          placeholder: undefined,
          childHandles,
          childTrace: element.childTrace,
          factorySource: element.factorySource,
          key: element.key,
          props: element.props,
        });
    listeners.forEach(l => { l.createdElement(node, element.props) });
    return {
      childHandles,
      last: node,
    };
  }
}

// Returns where a hydrated node goes, which is after left or first when nothing is left of it. A null
// nextSibling means left is the last child, so the node goes at the end rather than the front.
function nodeAfter(parent: Element, left: Node|undefined): Node|null {
  return left ? left.nextSibling : parent.firstChild;
}

class VdomCaching {

  on: boolean = true;

  disable(): void {
    this.on = false;
  }

  enable(): void {
    this.on = true;
  }
}

export const vdomCaching = new VdomCaching();

const TAG_TO_NAMESPACE = new Map([
  ['circle', 'http://www.w3.org/2000/svg'],
  ['clipPath', 'http://www.w3.org/2000/svg'],
  ['defs', 'http://www.w3.org/2000/svg'],
  ['g', 'http://www.w3.org/2000/svg'],
  ['line', 'http://www.w3.org/2000/svg'],
  ['path', 'http://www.w3.org/2000/svg'],
  ['pattern', 'http://www.w3.org/2000/svg'],
  ['polygon', 'http://www.w3.org/2000/svg'],
  ['polyline', 'http://www.w3.org/2000/svg'],
  ['rect', 'http://www.w3.org/2000/svg'],
  ['svg', 'http://www.w3.org/2000/svg'],
  ['text', 'http://www.w3.org/2000/svg'],
  ['title', 'http://www.w3.org/2000/svg'],
]);

const createdElements = new WeakMap<Handle, PhysicalElement>();
const creationTrace: Array<Handle[]> = [];
const lastCreationTrace: Array<Handle[]> = [];

function* createElement(element: VElementOrPrimitive, handle: Handle, parent: Element):
    Generator<Node, void, void> {
  if (!(element instanceof Object)) {
    const node = new Text(String(element));
    createdElements.set(
        handle, {
          parent,
          self: node,
          placeholder: undefined,
          childHandles: [],
          childTrace: [],
          factorySource: undefined,
          key: undefined,
          props: {},
        });
    yield node;
    return;
  }

  checkUniqueKeys(element.children);
  const childHandles = [];
  for (const child of element.children) {
    childHandles.push(maybeCreateHandle(child));
  }

  if (element.tag === FRAGMENT_MARKER) {
    const created = [];
    for (let i = 0; i < element.children.length; ++i) {
      const child = element.children[i];
      const handle = childHandles[i];
      for (const r of createElement(child, handle, parent)) {
        created.push(r);
      }
    }
    let placeholder = new Text('');
    createdElements.set(
        handle, {
          parent,
          self: undefined,
          placeholder,
          childHandles,
          childTrace: element.childTrace,
          factorySource: element.factorySource,
          key: element.key,
          props: element.props,
        });
    if (childHandles.length === 0) {
      yield placeholder;
    } else {
      for (const child of created) {
        yield child;
      }
    }
  } else {
    const namespace = TAG_TO_NAMESPACE.get(element.tag) ?? 'http://www.w3.org/1999/xhtml';
    const root = document.createElementNS(namespace, element.tag);
    appendChildrenToRoot(element.children, childHandles, root);
    patchProperties(root, {}, element.props);
    createdElements.set(
        handle, {
          parent,
          self: root,
          placeholder: undefined,
          childHandles,
          childTrace: element.childTrace,
          factorySource: element.factorySource,
          key: element.key,
          props: element.props,
        });
    listeners.forEach(l => { l.createdElement(root, element.props) });
    yield root;
  }
}

function appendChildrenToRoot(
    children: VElementOrPrimitive[], childHandles: Handle[], root: Element) {
  checkArgument(children.length === childHandles.length, 'Mismatched child elements and handles');
  for (let i = 0; i < children.length; ++i) {
    const child = children[i];
    const handle = childHandles[i];
    for (const result of createElement(child, handle, root)) {
      root.append(result);
    }
  }
}

// Reconciles `is` against `was`. Keyed `is` children claim the `was` child
// with the same key; unkeyed `is` children claim the next unclaimed unkeyed
// `was` child by relative position. We then walk the matches in `is` order,
// moving any reused child whose `wasIndex` regressed below the highest one
// already placed. With no keys, every `wasIndex` is non-decreasing — no
// moves happen and the DOM ops match the pre-keys algorithm.
function patchChildren(
    parent: Element, was: Handle[], is: VElementOrPrimitive[], placeholder: Node|undefined): {
      childHandles: Handle[];
      last: Node|undefined;
    } {
  const {matches, claimed} = matchChildren(was, is);
  const newHandles: Handle[] = [];
  let last: Node|undefined = undefined;
  let lastPlacedIndex = -1;

  // Anchor for inserts that happen before we've placed anything: the first
  // existing DOM node, falling back to the empty-fragment placeholder.
  let initialAnchor: Node|null = placeholder ?? null;
  for (const handle of was) {
    const physical = createdElements.get(handle);
    if (!physical) continue;
    const dom = firstDomNodeOf(physical);
    if (dom !== undefined) {
      initialAnchor = dom;
      break;
    }
  }

  for (const match of matches) {
    const isElement = match.isElement;
    if (match.wasIndex === undefined) {
      const handle = maybeCreateHandle(isElement);
      const adding = [...createElement(isElement, handle, parent)];
      const next = last ? last.nextSibling : initialAnchor;
      for (const a of adding) {
        parent.insertBefore(a, next);
        last = a;
      }
      newHandles.push(handle);
      continue;
    }

    const wasIndex = match.wasIndex;
    const wasHandle = was[wasIndex];
    const wasElement = checkExists(createdElements.get(wasHandle));

    if (wasIndex < lastPlacedIndex) {
      const target = last ? last.nextSibling : initialAnchor;
      for (const node of gatherDomNodes(wasElement)) {
        parent.insertBefore(node, target);
      }
    } else {
      lastPlacedIndex = wasIndex;
    }

    if (wasElement.self === undefined
        && (isElement instanceof Object && isElement.tag === FRAGMENT_MARKER)) {
      const handle = maybeCreateHandle(isElement);
      newHandles.push(handle);

      if (vdomCaching.on
          && isElement.factorySource
          && deepEqual(wasElement.factorySource, isElement.factorySource)) {
        // TODO: do we need to create everything?
        createdElements.set(handle, wasElement);
        last = findLastChildOrPlaceholder(wasElement);
        continue;
      }

      const fragmentPlaceholder = wasElement.placeholder;
      const result =
          patchChildren(parent, wasElement.childHandles, isElement.children, fragmentPlaceholder);
      last = result.last ?? last;
      // Mutate the existing record rather than swapping in a new one, the way
      // patchNode does for regular elements. Every render mints a fresh handle
      // and the cache fast path above aliases the new handle onto this very
      // record, so more than one handle can name it. A handle we already handed
      // out has to keep observing updates — in particular the one a bound
      // controller's updateState closed over, since that closure is only
      // re-pointed by patchController, which the fast path skips. Replacing the
      // object instead would leave that handle resolving to a frozen copy and
      // the controller's updates invisible to the parent's next render.
      wasElement.parent = parent;
      wasElement.placeholder = fragmentPlaceholder;
      wasElement.childHandles = result.childHandles;
      wasElement.childTrace = isElement.childTrace;
      wasElement.factorySource = isElement.factorySource;
      wasElement.key = isElement.key;
      wasElement.props = isElement.props;
      createdElements.set(handle, wasElement);
    } else if (wasElement.self === undefined) {
      const result =
          patchChildren(parent, wasElement.childHandles, [isElement], wasElement.placeholder);
      last = result.last ?? last;
      arrays.pushInto(newHandles, result.childHandles);
    } else if (!(isElement instanceof Object)) {
      const handle = createHandle();
      const replacements = [...createElement(isElement, handle, parent)];
      const sibling = wasElement.self.nextSibling;
      parent.replaceChild(replacements[0], wasElement.self);
      for (const listener of listeners) {
        listener.removedNode(wasElement.self);
      }
      replacements.slice(1).forEach(r => {parent.insertBefore(r, sibling)});
      newHandles.push(handle);
      last = replacements[replacements.length - 1];
    } else if (isElement.tag === FRAGMENT_MARKER) {
      const handle = maybeCreateHandle(isElement);
      newHandles.push(handle);
      const fragmentPlaceholder = new Text('');
      const result =
          patchChildren(parent, [wasHandle], isElement.children, fragmentPlaceholder);
      last = result.last ?? last;
      createdElements.set(
          handle, {
            parent,
            self: undefined,
            placeholder: fragmentPlaceholder,
            childHandles: result.childHandles,
            childTrace: isElement.childTrace,
            factorySource: isElement.factorySource,
            key: isElement.key,
            props: isElement.props,
          });
    } else {
      newHandles.push(maybeCreateHandle(isElement));
      last = patchNode(wasElement, isElement) ?? last;
    }
  }

  if (placeholder && was.length === 0 && is.length > 0) {
    parent.removeChild(placeholder);
  }

  if (placeholder && is.length === 0) {
    // An empty fragment still needs one node in the DOM to mark where it is, so that the next
    // render has somewhere to insert. That node is the placeholder. Put it in now, while the old
    // content is still there to position it against.
    if (was.length > 0) {
      checkArgument(initialAnchor !== placeholder, 'Fragment children have no DOM nodes');
      parent.insertBefore(placeholder, initialAnchor);
    } else {
      checkArgument(
          placeholder.parentNode === parent,
          'Empty fragment placeholder is not in the DOM');
    }
    last = placeholder;
  }

  for (let i = 0; i < was.length; i++) {
    if (claimed[i]) continue;
    removeElement(parent, checkExists(createdElements.get(was[i])));
  }

  return {
    childHandles: newHandles,
    last,
  };
}

interface VdomMatch {
  wasIndex: number|undefined;
  isElement: VElementOrPrimitive;
}

function matchChildren(
    was: Handle[],
    is: VElementOrPrimitive[]): {matches: VdomMatch[]; claimed: boolean[]} {
  checkUniqueKeys(is);

  const wasKeyToIndex = new Map<string, number>();
  for (let i = 0; i < was.length; i++) {
    const wasElement = createdElements.get(was[i]);
    const k = wasElement?.key;
    if (k !== undefined) {
      // `was` keys came from a previous render that already passed
      // checkUniqueKeys; a dup here means our own bookkeeping went sideways.
      checkArgument(
          !wasKeyToIndex.has(k),
          `Duplicate key '${k}' in vdom previous siblings`);
      wasKeyToIndex.set(k, i);
    }
  }

  const claimed: boolean[] = new Array(was.length).fill(false);
  let unkeyedCursor = 0;
  const matches: VdomMatch[] = [];

  for (const isElement of is) {
    const isKey = isElement instanceof Object ? isElement.key : undefined;
    let wasIndex: number|undefined = undefined;

    if (isKey !== undefined) {
      const idx = wasKeyToIndex.get(isKey);
      if (idx !== undefined && !claimed[idx]) {
        wasIndex = idx;
        claimed[idx] = true;
      }
    } else {
      while (unkeyedCursor < was.length) {
        const i = unkeyedCursor++;
        if (claimed[i]) continue;
        const wasElement = createdElements.get(was[i]);
        if (wasElement?.key === undefined) {
          wasIndex = i;
          claimed[i] = true;
          break;
        }
      }
    }

    matches.push({wasIndex, isElement});
  }

  return {matches, claimed};
}

function checkUniqueKeys(children: VElementOrPrimitive[]): void {
  const seen = new Set<string>();
  for (const child of children) {
    if (!(child instanceof Object) || child.key === undefined) continue;
    checkArgument(
        !seen.has(child.key),
        `Duplicate key '${child.key}' in vdom siblings`);
    seen.add(child.key);
  }
}

function factoryProps(props: Properties|null, children: VElementOrPrimitive[]): Properties {
  return {
    ...props,
    children: children.length > 0 || props?.children === undefined ? children : props.children,
  };
}

function* gatherDomNodes(physical: PhysicalElement): Generator<Node, void, void> {
  if (physical.self !== undefined) {
    yield physical.self;
    return;
  }
  if (physical.childHandles.length === 0) {
    if (physical.placeholder) yield physical.placeholder;
    return;
  }
  for (const handle of physical.childHandles) {
    const child = createdElements.get(handle);
    if (child) yield* gatherDomNodes(child);
  }
}

// Removes every DOM node an element put on the page, including the placeholders that mark its
// empty fragments.
function removeElement(parent: Element, physical: PhysicalElement): void {
  if (physical.self !== undefined) {
    parent.removeChild(physical.self);
    for (const listener of listeners) {
      listener.removedNode(physical.self);
    }
    return;
  }

  for (const handle of physical.childHandles) {
    const child = createdElements.get(handle);
    if (child) {
      removeElement(parent, child);
    }
  }

  if (physical.childHandles.length === 0) {
    const ownPlaceholder = checkExists(physical.placeholder);
    checkArgument(
        ownPlaceholder.parentNode === parent,
        'Empty fragment placeholder is not in the DOM');
    parent.removeChild(ownPlaceholder);
  }
}

function firstDomNodeOf(physical: PhysicalElement): Node|undefined {
  for (const n of gatherDomNodes(physical)) return n;
  return undefined;
}

function patchNode(physical: PhysicalElement, to: VElement): Node|undefined {
  checkArgument(to.tag !== FRAGMENT_MARKER, 'patchNode cannot patch fragments');
  const self = checkExists(physical.self, 'patchNode cannot patch fragments');

  if (!(self instanceof Element) || to.tag !== self.tagName.toLowerCase()) {
    const parent = physical.parent;
    const replacements = [...createElement(to, to.handle, parent)];
    if (replacements.length === 0) {
      parent.removeChild(self);
      listeners.forEach(l => { l.removedNode(self) });
      return undefined;
    }
    const sibling = self.nextSibling;
    parent.replaceChild(replacements[0], self);
    listeners.forEach(l => { l.removedNode(self) });
    replacements.slice(1).forEach(r => {parent.insertBefore(r, sibling)});
    return replacements[replacements.length - 1];
  }

  createdElements.set(to.handle, physical);

  if (vdomCaching.on && to.factorySource && deepEqual(physical.factorySource, to.factorySource)) {
    return self;
  }

  const oldProps = physical.props;
  patchProperties(self, oldProps, to.props);
  physical.factorySource = to.factorySource;
  physical.props = to.props;
  const {childHandles} = patchChildren(self, physical.childHandles, to.children, undefined);
  physical.childHandles = childHandles;
  listeners.forEach(l => { l.patchedElement(self, oldProps, to.props) });

  return self;
}

function patchProperties(element: Element, from: AnyProperties, to: AnyProperties) {
  const oldPropKeys = Object.keys(from) as Array<keyof AnyProperties>;
  const newPropKeys = Object.keys(to) as Array<keyof AnyProperties>;
  for (const key of newPropKeys) {
    if (key === 'js' || key === 'key' || key === 'unboundEvents') {
      continue;
    }

    if (!deepEqual(from[key], to[key])) {
      const value = to[key];
      if (key === 'autofocus') {
        if (value && element instanceof HTMLElement) {
          // Give the element a chance to be added to the page
          setTimeout(() => { element.focus(); }, 0);
        }
      } else if (key === 'className') {
        if (value) {
          element.setAttribute('class', String(value));
        } else {
          element.removeAttribute('class');
        }
      } else if (key === 'data') {
        const dataset = (element as HTMLElement).dataset;
        for (const k of Object.keys(dataset)) {
          delete dataset[k];
        }

        if (value) {
          for (const [k, v] of Object.entries(value)) {
            dataset[k] = v;
          }
        }
      } else if (
        key === 'value'
          && (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
        if (value !== undefined) { // don't clear value if we are no longer forcing it
          // Only overwrite the value if the element isn't currently focused. If it's focused, the
          // user may be actively typing.
          //
          // There is an unfortunate edge case here: if some controller really wants to overwrite
          // what the user typed, then we have no way to actually do it here. But in that case I
          // think it's okay to make people drop down to raw DOM calls and
          // * blur the element prior to changing its value
          // * directly set element.value rather than asking Corgi to do it. Corgi wouldn't know
          //   about the change and there might be consequences, but that seems acceptable.
          if (document.activeElement !== element) {
            // Only update if the value actually changed to avoid unnecessary DOM mutations
            const newValue = String(value);
            if (element.value !== newValue) {
              element.value = newValue;
            }
          }
        }
      } else {
        const canonical = canonicalize(key);
        const value = to[key] as boolean|number|string|undefined;
        if (value === undefined) {
          element.removeAttribute(key);
        } else if (typeof value === 'boolean') {
          if (canonical === 'checked' && element instanceof HTMLInputElement) {
            element.checked = value;
          } else if (canonical === 'indeterminate' && element instanceof HTMLInputElement) {
            element.indeterminate = value;
          } else if (canonical === 'selected' && element instanceof HTMLOptionElement) {
            element.selected = value;
          } else if (canonical === 'draggable' && element instanceof HTMLElement) {
            element.draggable = value;
          } else if (value) {
            element.setAttribute(canonical, '');
          } else {
            // TODO(april): is there a reason we shouldn't do this?
            element.removeAttribute(canonical);
          }
        } else {
          element.setAttribute(canonical, String(value));
        }
      }
    }
  }

  for (const key of oldPropKeys) {
    const canonical = canonicalize(key);
    if (!to.hasOwnProperty(key)) {
      if (key === 'data') {
        const dataset = (element as HTMLElement).dataset ?? {};
        for (const k of Object.keys(dataset)) {
          delete dataset[k];
        }
      } else {
        element.removeAttribute(key === 'className' ? 'class' : canonical);
      }
    } else if (typeof to[key] === 'boolean' && !to[key]) {
      element.removeAttribute(canonical);
    }
  }
}

let nextElementId = 0;

// SVG attribute names that the spec defines as camelCase. The browser's
// SVG parser reads these case-sensitively (`patternUnits`, not
// `pattern-units`), so they must skip the kebab-case conversion that
// HTML attributes go through.
const SVG_CAMELCASE_ATTRS = new Set([
  'attributeName',
  'attributeType',
  'baseFrequency',
  'baseProfile',
  'calcMode',
  'clipPathUnits',
  'diffuseConstant',
  'edgeMode',
  'filterUnits',
  'glyphRef',
  'gradientTransform',
  'gradientUnits',
  'kernelMatrix',
  'kernelUnitLength',
  'keyPoints',
  'keySplines',
  'keyTimes',
  'lengthAdjust',
  'limitingConeAngle',
  'markerHeight',
  'markerUnits',
  'markerWidth',
  'maskContentUnits',
  'maskUnits',
  'numOctaves',
  'pathLength',
  'patternContentUnits',
  'patternTransform',
  'patternUnits',
  'pointsAtX',
  'pointsAtY',
  'pointsAtZ',
  'preserveAlpha',
  'preserveAspectRatio',
  'primitiveUnits',
  'refX',
  'refY',
  'repeatCount',
  'repeatDur',
  'requiredExtensions',
  'requiredFeatures',
  'specularConstant',
  'specularExponent',
  'spreadMethod',
  'startOffset',
  'stdDeviation',
  'stitchTiles',
  'surfaceScale',
  'systemLanguage',
  'tableValues',
  'targetX',
  'targetY',
  'textLength',
  'viewBox',
  'viewTarget',
  'xChannelSelector',
  'yChannelSelector',
  'zoomAndPan',
]);

export function canonicalize(key: string): string {
  if (SVG_CAMELCASE_ATTRS.has(key)) {
    return key;
  } else {
    let k = '';
    for (let c of key) {
      if ('A' <= c && c <= 'Z') {
        k = `${k}-${c.toLowerCase()}`;
      } else {
        k = `${k}${c}`;
      }
    }
    return k;
  }
}

function createHandle(): Handle {
  return {id: ++nextElementId} as Handle;
}

type Nested<V> = V|readonly Nested<V>[];

function deepFlatten<V>(items: readonly Nested<V>[]): V[] {
  const flattened: V[] = [];
  for (const item of items) {
    if (isNested(item)) {
      flattened.push(...deepFlatten(item));
    } else if (item !== undefined && item !== null && item !== false && item !== true) {
      flattened.push(item);
    }
  }
  return flattened;
}

function isNested<V>(item: Nested<V>): item is readonly Nested<V>[] {
  return Array.isArray(item);
}

function findLastChildOrPlaceholder(element: PhysicalElement): Node {
  if (element.self) {
    return element.self;
  } else {
    if (element.childHandles.length > 0) {
      const last = element.childHandles[element.childHandles.length - 1];
      const child = checkExists(createdElements.get(last));
      return findLastChildOrPlaceholder(child);
    } else {
      return checkExists(element.placeholder);
    }
  }
}

function maybeCreateHandle(element: VElementOrPrimitive): Handle {
  if (element instanceof Object) {
    return element.handle;
  } else {
    return createHandle();
  }
}

export function Fragment({children}: {children: VElementOrPrimitive[]}): VElement {
  return {
    tag: FRAGMENT_MARKER,
    children,
    childTrace: [],
    handle: createHandle(),
    key: undefined,
    factorySource: undefined,
    props: {},
  };
}

/**
 * Holds what a function component returned in a fragment. An array is flattened as children are,
 * so that each of its elements is a child and not the array itself.
 */
function wrap(element: Rendered): VElement {
  return Fragment({children: deepFlatten([element])});
}

